import apiClient from './AuthService';

export interface PersonalityTraitsChallenge {
  personality_traits_challenge_id?: string;
  project_id: string;
  user_id?: string;
  member_id: string;
  member_name?: string;
  traits: string;
  together?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PersonalityTraitsChallengeResponse {
  personality_traits_challenge_id: string;
  message: string;
}

export interface PersonalityTraitsChallengeData {
  data: PersonalityTraitsChallenge | PersonalityTraitsChallenge[];
}

export const savePersonalityTraitsChallenge = async (
  projectId: string,
  memberId: string,
  traits: string,
  together: boolean = false
): Promise<PersonalityTraitsChallengeResponse> => {
  try {
    const response = await apiClient.post<PersonalityTraitsChallengeResponse>(
      '/quiz/personality-traits-challenge',
      {
        project_id: projectId,
        member_id: memberId,
        traits: traits,
        together: together
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Error al guardar el test de personalidad. Por favor, intenta de nuevo.');
  }
};

export const getPersonalityTraitsChallenge = async (
  personalityTraitsChallengeId: string
): Promise<PersonalityTraitsChallenge> => {
  try {
    const response = await apiClient.get<PersonalityTraitsChallengeData>(
      `/quiz/personality-traits-challenge/${personalityTraitsChallengeId}`
    );
    return response.data.data as PersonalityTraitsChallenge;
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const getProjectPersonalityTraitsChallenges = async (
  projectId: string
): Promise<PersonalityTraitsChallenge[]> => {
  try {
    const response = await apiClient.get<PersonalityTraitsChallengeData>(
      `/quiz/project/${projectId}/personality-traits-challenges`
    );
    return response.data.data as PersonalityTraitsChallenge[];
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const getMemberPersonalityTraitsChallenges = async (
  memberId: string
): Promise<PersonalityTraitsChallenge[]> => {
  try {
    const response = await apiClient.get<PersonalityTraitsChallengeData>(
      `/quiz/member/${memberId}/personality-traits-challenges`
    );
    return response.data.data as PersonalityTraitsChallenge[];
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const checkMemberHasCompletedChallenge = async (
  memberId: string
): Promise<boolean> => {
  try {
    const challenges = await getMemberPersonalityTraitsChallenges(memberId);
    return challenges && challenges.length > 0;
  } catch (error) {
    return false;
  }
};

export const checkAllMembersCompleted = async (
  projectId: string,
  memberIds: string[]
): Promise<{
  allCompleted: boolean;
  completedMembers: string[];
  mode: 'JUNTOS' | 'POR_SEPARADO' | null;
  challenges: PersonalityTraitsChallenge[];
}> => {
  try {
    const challenges = await getProjectPersonalityTraitsChallenges(projectId);
    const completedMemberIds = challenges.map(c => c.member_id);

    const allCompleted = memberIds.every(memberId =>
      completedMemberIds.includes(memberId)
    );

    // Determine mode based on existing challenges
    let mode: 'JUNTOS' | 'POR_SEPARADO' | null = null;
    if (challenges.length > 0) {
      // Check the 'together' field of the first challenge to determine mode
      mode = challenges[0].together === true ? 'JUNTOS' : 'POR_SEPARADO';
    }

    return {
      allCompleted,
      completedMembers: completedMemberIds,
      mode,
      challenges
    };
  } catch (error) {
    return {
      allCompleted: false,
      completedMembers: [],
      mode: null,
      challenges: []
    };
  }
};